import { MAX_RENDER_DURATION_SECONDS } from "./presets.js";

/**
 * Returns the exact number of encoded frames in a deterministic film.
 *
 * @param durationSeconds - Positive integer duration.
 * @param framesPerSecond - Positive integer frame rate.
 * @returns Product of duration and frame rate.
 * @throws RangeError for invalid or excessive inputs.
 */
export function frameCount(durationSeconds: number, framesPerSecond: number): number {
  if (
    !Number.isInteger(durationSeconds)
    || !Number.isInteger(framesPerSecond)
    || durationSeconds < 1
    || durationSeconds > MAX_RENDER_DURATION_SECONDS
    || framesPerSecond < 1
  ) {
    throw new RangeError("Timeline duration and frame rate must be positive integers.");
  }
  const count = durationSeconds * framesPerSecond;
  if (count > MAX_RENDER_DURATION_SECONDS * 30) {
    throw new RangeError(`Timeline exceeds the ${MAX_RENDER_DURATION_SECONDS}-second render limit at 30 fps.`);
  }
  return count;
}

/**
 * Maps a zero-based frame to route progress, including both endpoints.
 *
 * @param frameIndex - Zero-based frame index.
 * @param totalFrames - Total frame count, at least two.
 * @returns Fraction from zero through one.
 * @throws RangeError when the index or total is invalid.
 */
export function frameProgress(frameIndex: number, totalFrames: number): number {
  if (
    !Number.isInteger(frameIndex)
    || !Number.isInteger(totalFrames)
    || totalFrames < 2
    || frameIndex < 0
    || frameIndex >= totalFrames
  ) {
    throw new RangeError("Frame index must fall within a timeline of at least two frames.");
  }
  return frameIndex / (totalFrames - 1);
}

/** Samples a short clip from the complete film without changing route playback speed. */
export function clipFrameProgress(
  frameIndex: number,
  clipFrames: number,
  filmFrames: number,
  startFrame = 0,
): number {
  if (
    !Number.isInteger(startFrame)
    || startFrame < 0
    || !Number.isInteger(clipFrames)
    || clipFrames < 2
    || startFrame + clipFrames > filmFrames
    || frameIndex < 0
    || frameIndex >= clipFrames
  ) {
    throw new RangeError("Clip frames must fit within the complete film timeline.");
  }
  return frameProgress(startFrame + frameIndex, filmFrames);
}
